import { definePage, type PluginWebManifest } from '@reflex/plugin-api/web';
import { ids } from './shared/ids.js';

const page = definePage({
  path: '/configure/network-policies',
  label: 'Network Policies',
  icon: 'Shield',
  group: 'organization',
  componentPath: '@reflex/plugin-network-policy/web/OrganizationSecurityPage',
  description: 'Network policies that restrict devbox egress.',
  details:
    'Manage the Network Policies for this organization and pick the org default. Each policy defines egress rules that Runloop enforces at the devbox level: allow all traffic, allow specific hostnames, or gate access to the agent gateway and MCP hub. The default policy applies to every devbox launched without an explicitly attached policy.',
  features: [
    'Reusable network policies managed per organization',
    'Org default policy applied to every devbox launch',
    'Attach a policy to an agent run from the new-agent picker',
    'Runloop enforces the policy at the devbox level',
    'Network Policy section on every agent detail page',
  ],
});

export const networkPolicyWeb: PluginWebManifest = {
  ...page.web,
  routes: [
    ...(page.web.routes ?? []),
    // The retired standalone management URL. Kept mounted so old bookmarks
    // and links forward to the Organization Security page.
    {
      path: '/network-policies',
      componentPath: '@reflex/plugin-network-policy/web/NetworkPoliciesRedirect',
    },
  ],
  // The plugin detail page slide-over keeps the self-contained management
  // component; the daily-use surface is the Organization Security page.
  orgSetupComponent: {
    componentPath: '@reflex/plugin-network-policy/web/NetworkPoliciesPage',
    label: 'Create a Network Policy to start restricting devbox egress.',
  },
  // Org default policy picker on the org Settings page, backed by the
  // host's per-org plugin settings routes (`plugins:read` to view,
  // `plugins:manage` to change).
  orgSettingsSections: [
    {
      id: 'network-policy-default',
      label: 'Network policy',
      icon: 'Shield',
      componentPath: '@reflex/plugin-network-policy/web/NetworkPolicyOrgSettingsSection',
    },
  ],
  attachments: [
    {
      id: 'network-policies',
      label: 'Network Policy',
      icon: 'Shield',
      pickerComponentPath: '@reflex/plugin-network-policy/web/NetworkPolicyAttachmentPicker',
      summaryComponentPath: '@reflex/plugin-network-policy/web/NetworkPolicyAttachmentSummary',
    },
  ],
  launchMentionProviders: [
    {
      id: 'launch-mention-network-policy',
      label: 'Network Policies',
      keyword: 'policy',
      icon: 'Shield',
      componentPath: '@reflex/plugin-network-policy/web/NetworkPolicyMentionProvider',
      createComponentPath: '@reflex/plugin-network-policy/web/NetworkPolicyMentionCreate',
      createLabel: 'Add network policy',
      order: 50,
    },
  ],
  agentDetailSections: [
    {
      id: 'network-policies',
      label: 'Network Policy',
      icon: 'Shield',
      componentPath: '@reflex/plugin-network-policy/web/NetworkPolicyDetailSection',
      order: 12,
    },
  ],
  resourcePreviews: [
    {
      kind: 'network-policies',
      label: 'Network Policy',
      icon: 'Shield',
      componentPath: '@reflex/plugin-network-policy/web/NetworkPolicyPreview',
      detailHref: '/configure/network-policies',
      // Lets a bare `npl_…` id anywhere in the app (chat output, Chief's
      // approval card) resolve this preview's icon and hover card.
      idPrefixes: [...ids.prefixes],
    },
  ],
};
